"use client";

import { useEffect, useRef, useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  LABEL,
  TEXTAREA_PROSE,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { getLocaleMeta } from "@/lib/i18n/locales";
import { downloadText } from "@/lib/tool-kit/download";
import {
  appendPhrase,
  applySpokenPunctuation,
  countWords,
} from "@/lib/tool-kit/study/dictation";

/** Recognition languages offered, as BCP 47 tags. Names come from `Intl.DisplayNames`. */
const LANGUAGES = [
  "en-US",
  "en-GB",
  "en-AU",
  "en-CA",
  "en-IN",
  "es-ES",
  "es-MX",
  "es-US",
  "fr-FR",
  "fr-CA",
  "de-DE",
  "it-IT",
  "pt-BR",
  "pt-PT",
  "nl-NL",
  "pl-PL",
  "tr-TR",
  "ru-RU",
  "uk-UA",
  "id-ID",
  "vi-VN",
  "hi-IN",
  "ja-JP",
  "ko-KR",
  "zh-CN",
  "zh-TW",
  "ar-SA",
  "sv-SE",
  "da-DK",
  "nb-NO",
  "fi-FI",
  "cs-CZ",
  "el-GR",
  "he-IL",
  "th-TH",
  "ro-RO",
  "hu-HU",
] as const;
const ENGLISH = /^en\b/i;
/** Restarts in a row with no speech before continuous mode gives up. */
const MAX_IDLE_RESTARTS = 8;

interface Alternative {
  transcript: string;
}
interface RecognitionResult {
  readonly isFinal: boolean;
  readonly [index: number]: Alternative;
}
interface RecognitionResultEvent {
  readonly resultIndex: number;
  readonly results: {
    readonly length: number;
    readonly [index: number]: RecognitionResult;
  };
}
interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: RecognitionResultEvent) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type RecognitionConstructor = new () => Recognition;

function recognitionConstructor(): RecognitionConstructor | null {
  const scope = window as unknown as {
    SpeechRecognition?: RecognitionConstructor;
    webkitSpeechRecognition?: RecognitionConstructor;
  };
  return scope.SpeechRecognition ?? scope.webkitSpeechRecognition ?? null;
}

/** The reader's language if we offer it, else the page's, else US English. */
function defaultLanguage(pageLocale: string): string {
  const wanted = [
    navigator.language,
    getLocaleMeta(pageLocale)?.hreflang ?? "en",
  ];
  for (const tag of wanted) {
    const exact = LANGUAGES.find(
      (language) => language.toLowerCase() === tag.toLowerCase()
    );
    const base = LANGUAGES.find(
      (language) => language.split("-")[0] === tag.split("-")[0].toLowerCase()
    );
    if (exact ?? base) {
      return exact ?? base ?? "en-US";
    }
  }
  return "en-US";
}

/** English strings. A translated content file overrides these through `ui`. */
export const SPEECH_TO_TEXT_STRINGS = {
  languageLabel: "Language you'll speak",
  start: "Start dictation",
  stop: "Stop",
  listening: "Listening — speak now",
  idle: "Not listening",
  continuous: "Keep listening through pauses",
  punctuation:
    'Spoken punctuation: say "comma", "period", "question mark", "exclamation mark" or "new line"',
  punctuationEnglishOnly: "Spoken punctuation works for English only.",
  transcriptLabel: "Transcript (edit it freely)",
  transcriptPlaceholder:
    "Press Start dictation and speak. Your words appear here.",
  hearing: "Hearing: {text}",
  wordCount: "one:{count} word|other:{count} words",
  copy: "Copy text",
  copied: "Copied!",
  download: "Download .txt",
  fileName: "transcript.txt",
  clear: "Clear",
  confirmClear: "Click again to clear",
  checking: "Checking whether this browser supports speech recognition…",
  unsupported:
    "This browser doesn't offer speech recognition. It works in Google Chrome and Microsoft Edge on computers and Android, and in Safari on Mac, iPhone and iPad; Firefox does not support it.",
  privacyHeading: "Where your voice goes",
  privacyBody:
    "Speech is turned into text by your browser's speech service, not by CitationEasy. In Google Chrome, the audio is sent to Google's servers to be transcribed; Microsoft Edge sends it to Microsoft's online speech service; Safari uses Apple's speech recognition, which can also process audio on Apple's servers. CitationEasy never receives your audio or your transcript, and the text stays in this page until you copy or download it. Don't dictate anything you wouldn't want those services to process.",
  errors: {
    "not-allowed":
      "Microphone access was blocked. Allow the microphone for this site in your browser's settings, then try again.",
    "service-not-allowed":
      "The browser or your system settings blocked the speech service. In Safari, check that Siri or Dictation is turned on.",
    "audio-capture": "No microphone was found. Connect one and try again.",
    network:
      "The browser couldn't reach its speech service. Check your connection — some browsers include the feature but have no service behind it.",
    "no-speech":
      "No speech was heard. Check your microphone and speak a little closer to it.",
    "language-not-supported":
      "Your browser's speech service doesn't support that language. Choose another one.",
    other:
      "Speech recognition stopped unexpectedly. Press Start dictation to try again.",
  },
} as const;

type ErrorKey = keyof typeof SPEECH_TO_TEXT_STRINGS.errors;
type Support = "checking" | "yes" | "no";

export default function SpeechToTextWidget() {
  const strings = useToolUi(SPEECH_TO_TEXT_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [support, setSupport] = useState<Support>("checking");
  const [language, setLanguage] = useState<string>("en-US");
  const [names, setNames] = useState<Record<string, string>>({});
  const [continuous, setContinuous] = useState(true);
  const [punctuation, setPunctuation] = useState(true);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interim, setInterim] = useState("");
  const [error, setError] = useState<ErrorKey | null>(null);
  const [clearArmed, setClearArmed] = useState(false);
  const recognition = useRef<Recognition | null>(null);
  const wanted = useRef(false);
  const idleRestarts = useRef(0);
  const settings = useRef({ language, punctuation, continuous });
  settings.current = { language, punctuation, continuous };

  useEffect(() => {
    setSupport(recognitionConstructor() ? "yes" : "no");
    setLanguage(defaultLanguage(locale));
    try {
      const display = new Intl.DisplayNames(
        [getLocaleMeta(locale)?.hreflang ?? "en"],
        {
          type: "language",
        }
      );
      setNames(
        Object.fromEntries(
          LANGUAGES.map((tag) => [tag, display.of(tag) ?? tag])
        )
      );
    } catch {
      // Without DisplayNames the options show their language tags.
    }
    return () => {
      wanted.current = false;
      recognition.current?.abort();
    };
  }, [locale]);

  function handleResult(event: RecognitionResultEvent) {
    idleRestarts.current = 0;
    let pending = "";
    for (
      let index = event.resultIndex;
      index < event.results.length;
      index += 1
    ) {
      const result = event.results[index];
      const text = result[0]?.transcript ?? "";
      if (!result.isFinal) {
        pending += text;
        continue;
      }
      const { language: lang, punctuation: spoken } = settings.current;
      const phrase =
        spoken && ENGLISH.test(lang) ? applySpokenPunctuation(text) : text;
      setTranscript((current) => appendPhrase(current, phrase, lang));
    }
    setInterim(pending);
  }

  function begin() {
    const Constructor = recognitionConstructor();
    if (!Constructor) {
      return;
    }
    const session = new Constructor();
    session.lang = settings.current.language;
    session.continuous = settings.current.continuous;
    session.interimResults = true;
    session.onresult = handleResult;
    session.onerror = (event) => {
      if (event.error === "aborted") {
        return;
      }
      if (event.error === "no-speech" && settings.current.continuous) {
        return;
      }
      wanted.current = false;
      setError(
        event.error in strings.errors ? (event.error as ErrorKey) : "other"
      );
    };
    session.onend = () => {
      setInterim("");
      const keepGoing = wanted.current && settings.current.continuous;
      if (keepGoing && idleRestarts.current >= MAX_IDLE_RESTARTS) {
        setError("no-speech");
      }
      if (keepGoing && idleRestarts.current < MAX_IDLE_RESTARTS) {
        idleRestarts.current += 1;
        try {
          session.start();
          return;
        } catch {
          wanted.current = false;
        }
      }
      wanted.current = false;
      setListening(false);
    };
    recognition.current = session;
    try {
      session.start();
      wanted.current = true;
      idleRestarts.current = 0;
      setError(null);
      setListening(true);
    } catch {
      setError("other");
    }
  }

  function stop() {
    wanted.current = false;
    recognition.current?.stop();
  }

  function clear() {
    if (!clearArmed) {
      setClearArmed(true);
      return;
    }
    setTranscript("");
    setClearArmed(false);
  }

  const words = countWords(transcript);
  const english = ENGLISH.test(language);

  return (
    <>
      <section className="space-y-4">
        {support === "checking" ? <Notice>{strings.checking}</Notice> : null}
        {support === "no" ? (
          <ErrorNotice>{strings.unsupported}</ErrorNotice>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
          <SelectField
            id="stt-language"
            label={strings.languageLabel}
            onChange={(value) => {
              setLanguage(value);
              if (listening) {
                stop();
              }
            }}
            options={LANGUAGES.map((tag) => ({
              value: tag,
              label: names[tag] ? `${names[tag]} (${tag})` : tag,
            }))}
            value={language}
          />
          {listening ? (
            <button className={BUTTON_PRIMARY} onClick={stop} type="button">
              {strings.stop}
            </button>
          ) : (
            <button
              className={BUTTON_PRIMARY}
              disabled={support !== "yes"}
              onClick={begin}
              type="button"
            >
              {strings.start}
            </button>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <Checkbox
            checked={continuous}
            label={strings.continuous}
            onChange={setContinuous}
          />
          {english ? (
            <Checkbox
              checked={punctuation}
              label={strings.punctuation}
              onChange={setPunctuation}
            />
          ) : (
            <p className={HINT}>{strings.punctuationEnglishOnly}</p>
          )}
        </div>
        <p aria-live="polite" className="flex items-center gap-2 text-sm">
          <span
            aria-hidden="true"
            className={`inline-block h-2.5 w-2.5 rounded-full ${listening ? "animate-pulse bg-red-600" : "bg-muted-foreground/40"}`}
          />
          <span
            className={
              listening
                ? "font-medium text-foreground"
                : "text-muted-foreground"
            }
          >
            {listening ? strings.listening : strings.idle}
          </span>
        </p>
        {error ? <ErrorNotice>{strings.errors[error]}</ErrorNotice> : null}
      </section>

      <section className="space-y-3">
        <div className="space-y-2">
          <label className={LABEL} htmlFor="stt-transcript">
            {strings.transcriptLabel}
          </label>
          <textarea
            className={`${TEXTAREA_PROSE} min-h-[240px]`}
            id="stt-transcript"
            onChange={(event) => {
              setTranscript(event.target.value);
              setClearArmed(false);
            }}
            placeholder={strings.transcriptPlaceholder}
            value={transcript}
          />
          <p className="min-h-[1.25rem] text-muted-foreground text-sm italic">
            {interim ? t(strings.hearing, { text: interim }) : null}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copy}
            text={transcript}
          />
          <button
            className={BUTTON_SECONDARY}
            disabled={transcript === ""}
            onClick={() => downloadText(strings.fileName, transcript)}
            type="button"
          >
            {strings.download}
          </button>
          <button
            className={BUTTON_SMALL}
            disabled={transcript === ""}
            onClick={clear}
            type="button"
          >
            {clearArmed ? strings.confirmClear : strings.clear}
          </button>
          <span className={HINT}>
            {tPlural(strings.wordCount, locale, words, { count: fmt(words) })}
          </span>
        </div>
      </section>

      <section className="space-y-2 rounded-lg border border-border bg-muted/30 p-5">
        <h2 className={WIDGET_H3}>{strings.privacyHeading}</h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.privacyBody}
        </p>
      </section>
    </>
  );
}
